// Copyright (c) 2020-2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import map from "lodash/map";
import filter from "lodash/filter";
import './FormBuilder.scss';
import FormModel from "../../../../nor/ui/types/FormModel";
import TextField from "../../../../nor/ui/components/fields/text/TextField";
import Button from "../../../../nor/ui/components/button/Button";
import Icon from "../../../../nor/ui/components/icon/Icon";
import { AddIcon, DragIcon, EditIcon, TrashIcon } from "../../../../assets/icon";
import {ChangeCallback, VoidCallback} from "../../../../nor/ts/interfaces/callbacks";
import FormItem from "../../../../nor/ui/types/FormItem";
import FormItemType from "../../../../nor/ui/types/FormItemType";
import DropZone from "../../../../nor/ui/components/drag/DropZone";
import Draggable from "../../../../nor/ui/components/drag/Draggable";
import LogService from "../../../../nor/ts/LogService";
import Popup from "../../../../nor/ui/components/popup/Popup";
import SelectFieldModel from "../../../../nor/ui/types/items/SelectFieldModel";
import FormAppClassName from "../../../../constants/FormAppClassName";
import EditFieldModal from "../../../../modals/EditFieldModal";
import { find } from "../../../../nor/ts/modules/lodash";
import SliderFieldModel from "../../../../nor/ui/types/items/SliderFieldModel";

const LOG = LogService.createLogger('FormBuilder');

export interface FormBuilderState {

    readonly model            : FormModel;
    readonly modelHistory     : FormModel[];
    readonly actionsMenuOpen  : boolean;

}

export interface FormBuilderProps {

    readonly title    ?: string;
    readonly model     : FormModel;
    readonly onChange  : ChangeCallback<FormModel>;

}

export class FormBuilder extends React.Component<FormBuilderProps, FormBuilderState> {

    private readonly _changeTitleCallback        : ChangeCallback<string|undefined>;

    private readonly _changeCancelLabelCallback  : ChangeCallback<string | undefined>;
    private readonly _resetCancelLabelCallback   : VoidCallback;
    private readonly _deleteCancelLabelCallback  : VoidCallback;

    private readonly _changeSubmitLabelCallback  : ChangeCallback<string | undefined>;
    private readonly _resetSubmitLabelCallback   : VoidCallback;
    private readonly _deleteSubmitLabelCallback  : VoidCallback;

    private readonly _addPageCallback            : VoidCallback;
    private readonly _addTextFieldCallback       : VoidCallback;
    private readonly _addPasswordFieldCallback   : VoidCallback;
    private readonly _addEmailFieldCallback      : VoidCallback;
    private readonly _addTextAreaFieldCallback   : VoidCallback;
    private readonly _addJsonFieldCallback       : VoidCallback;
    private readonly _addIntegerFieldCallback    : VoidCallback;
    private readonly _addCheckboxFieldCallback   : VoidCallback;
    private readonly _addSelectFieldCallback     : VoidCallback;
    private readonly _addSliderFieldCallback     : VoidCallback;

    private readonly _toggleActionsMenuCallback  : VoidCallback;

    constructor(props: FormBuilderProps) {

        super(props);

        this.state = {
            model: {
                title: '',
                items: []
            },
            modelHistory: [],
            actionsMenuOpen: false
        };

        this._changeTitleCallback = this._changeTitle.bind(this);

        this._addPageCallback     = this._addPage.bind(this);

        this._addTextFieldCallback     = () => this._addItem({type: FormItemType.TEXT_FIELD});
        this._addPasswordFieldCallback = () => this._addItem({type: FormItemType.PASSWORD_FIELD});
        this._addEmailFieldCallback    = () => this._addItem({type: FormItemType.EMAIL_FIELD});
        this._addTextAreaFieldCallback = () => this._addItem({type: FormItemType.TEXT_AREA_FIELD});
        this._addJsonFieldCallback     = () => this._addItem({type: FormItemType.JSON_FIELD});
        this._addIntegerFieldCallback  = () => this._addItem({type: FormItemType.INTEGER_FIELD});
        this._addCheckboxFieldCallback = () => this._addItem({type: FormItemType.CHECKBOX_FIELD});
        this._addSelectFieldCallback   = () => this._addItem({type: FormItemType.SELECT_FIELD, values: []} as SelectFieldModel<any>);
        this._addSliderFieldCallback   = () => this._addItem({type: FormItemType.SLIDER_FIELD, values: []} as SliderFieldModel<any>);

        this._changeCancelLabelCallback = this._onChangeCancelLabel.bind(this);
        this._resetCancelLabelCallback  = this._onResetCancelLabel.bind(this);
        this._deleteCancelLabelCallback = this._onDeleteCancelLabel.bind(this);

        this._changeSubmitLabelCallback = this._onChangeSubmitLabel.bind(this);
        this._resetSubmitLabelCallback  = this._onResetSubmitLabel.bind(this);
        this._deleteSubmitLabelCallback = this._onDeleteSubmitLabel.bind(this);

        this._toggleActionsMenuCallback = this._onToggleActionsMenu.bind(this);

    }

    render () {

        const model : FormModel  = this.props.model;
        const title : string     = model?.title ?? '';
        const items : FormItem[] = model?.items ?? [];
        const cancelLabel : string | undefined = model?.cancelLabel;
        const submitLabel : string | undefined = model?.submitLabel;

        return (
            <div className={FormAppClassName.FORM_BUILDER}>

                <header className={FormAppClassName.FORM_BUILDER + '-header'}>

                    <h1 className={FormAppClassName.FORM_BUILDER + '-header-title'}>{this.props.title ?? "Builder"}</h1>

                    <Button
                        click={this._toggleActionsMenuCallback}
                        className={
                            FormAppClassName.FORM_BUILDER + '-action-menu-button '
                            + FormAppClassName.FORM_BUILDER + (this.state.actionsMenuOpen ? '-action-menu-open' : '-action-menu-closed')
                        }
                    ><Icon><AddIcon /></Icon></Button>

                    {this.props.children}

                </header>

                <Popup open={this.state.actionsMenuOpen}>

                    {cancelLabel !== undefined ? null : (
                        <Button click={this._resetCancelLabelCallback}><Icon><AddIcon/></Icon> Cancel button label</Button>
                    )}

                    {submitLabel !== undefined ? null : (
                        <Button click={this._resetSubmitLabelCallback}><Icon><AddIcon/></Icon> Submit button label</Button>
                    )}

                    <Button click={this._addTextFieldCallback}><Icon><AddIcon /></Icon> Text field</Button>

                    <Button click={this._addPasswordFieldCallback}><Icon><AddIcon /></Icon> Password field</Button>

                    <Button click={this._addEmailFieldCallback}><Icon><AddIcon /></Icon> Email field</Button>

                    <Button click={this._addTextAreaFieldCallback}><Icon><AddIcon /></Icon> Text area field</Button>

                    <Button click={this._addJsonFieldCallback}><Icon><AddIcon /></Icon> Json field</Button>

                    <Button click={this._addIntegerFieldCallback}><Icon><AddIcon /></Icon> Integer field</Button>

                    <Button click={this._addCheckboxFieldCallback}><Icon><AddIcon /></Icon> Checkbox field</Button>

                    <Button click={this._addSelectFieldCallback}><Icon><AddIcon /></Icon> Select field</Button>

                    <Button click={this._addSliderFieldCallback}><Icon><AddIcon /></Icon> Slider field</Button>

                    <Button click={this._addPageCallback}><Icon><AddIcon /></Icon> Page break</Button>

                </Popup>

                <section className={FormAppClassName.FORM_BUILDER + '-content'}>

                    <TextField
                        label='Main Title'
                        placeholder={'Title'}
                        value={title}
                        change={this._changeTitleCallback}
                    />

                    {map(items, (item: FormItem, itemIndex : number) => {

                        const editItemCallback    = () => this._editItem(itemIndex,);
                        const dropItemCallback    = (id: string, sourceItem : number) => this._swapItem(sourceItem, itemIndex);

                        const id = `form-page-item-${itemIndex}`;

                        const itemType = item?.type ?? '';
                        const itemKey  = (item as unknown as any)?.key ?? '';
                        const itemLabel = `${itemType}${itemKey?` #${itemKey}` : ''}`;

                        return (
                            <DropZone
                                key={id}
                                onDrop={dropItemCallback}
                            >
                                <Draggable
                                    id={`${id}-draggable`}
                                    data={[itemIndex]}
                                >

                                    <div className={FormAppClassName.FORM_BUILDER + '-content-item'}>
                                        <Icon className={FormAppClassName.FORM_BUILDER + '-content-item-drag-icon'}><DragIcon /></Icon>
                                        <div className={FormAppClassName.FORM_BUILDER + '-content-item-content'}>{itemLabel}</div>
                                        <Button className={FormAppClassName.FORM_BUILDER + '-content-item-edit-button'}
                                                click={editItemCallback}
                                        >
                                            <Icon className={FormAppClassName.FORM_BUILDER + '-content-item-edit-button-icon'}><EditIcon /></Icon>
                                        </Button>
                                    </div>

                                    {/*{isFormFieldType(item.type) ? (*/}
                                    {/*    <FieldBuilder*/}
                                    {/*        model={item as FormFieldModel}*/}
                                    {/*        onChange={changeItemCallback}*/}
                                    {/*        onDelete={deleteItemCallback}*/}
                                    {/*    />*/}
                                    {/*) : (*/}
                                    {/*    item?.type === FormItemType.PAGE_BREAK ? (*/}
                                    {/*        <PageBreakBuilder*/}
                                    {/*            model={item as PageBreakModel}*/}
                                    {/*            onChange={changeItemCallback}*/}
                                    {/*            onDelete={deleteItemCallback}*/}
                                    {/*        />*/}
                                    {/*    ) :*/}
                                    {/*    null*/}
                                    {/*)}*/}

                                </Draggable>
                            </DropZone>
                        );

                    })}

                    {cancelLabel !== undefined ? (
                        <div className={FormAppClassName.FORM_BUILDER + '-content-page-row'}>

                            <TextField
                                label='Cancel button'
                                placeholder={'Label'}
                                value={cancelLabel}
                                change={this._changeCancelLabelCallback}
                            >
                                <Button click={this._deleteCancelLabelCallback}><Icon><TrashIcon/></Icon></Button>
                            </TextField>

                        </div>
                    ) : null}

                    {submitLabel !== undefined ? (
                        <div className={FormAppClassName.FORM_BUILDER + '-content-page-row'}>

                            <TextField
                                label='Submit button'
                                placeholder={'Label'}
                                value={submitLabel}
                                change={this._changeSubmitLabelCallback}
                            >
                                <Button click={this._deleteSubmitLabelCallback}><Icon><TrashIcon/></Icon></Button>
                            </TextField>

                        </div>
                    ) : null}

                </section>

            </div>
        );

    }

    /**
     * Adds new item
     *
     * @param item
     * @private
     */
    private _addItem (item: FormItem) {

        const oldPage = this.props.model;

        const oldPageItems = oldPage?.items ?? [];

        const items = [...oldPageItems];

        items.push({...item});

        this._setModelItems(items);

        this._closeActionsMenu();

    }

    private _addPage () {

        this._addItem({
            type: FormItemType.PAGE_BREAK
        });

        this._closeActionsMenu();

    }

    /**
     * Replaces item at index `itemIndex` with `newItem`.
     *
     * @param itemIndex
     * @param newItem
     * @private
     */
    private _replaceItem (itemIndex: number, newItem: FormItem) {

        const oldModel = this.props.model;
        const oldItems = oldModel?.items ?? [];

        const newItems = [...oldItems];

        if (itemIndex >= newItems.length) {
            console.warn('Warning! No item found at ', itemIndex);
            return;
        }

        newItems[itemIndex] = newItem;

        this._setModelItems(newItems);

    }

    /**
     * Swaps items at index `sourceItemIndex` and `targetItemIndex`.
     *
     * @param sourceItemIndex
     * @param targetItemIndex
     * @private
     */
    private _swapItem (sourceItemIndex: number, targetItemIndex: number) {

        LOG.debug(`Dropped ${sourceItemIndex} over ${targetItemIndex} `);

        const oldModel = this.props.model;
        const oldItems = oldModel?.items ?? [];

        const newItems = [...oldItems];

        if (sourceItemIndex >= newItems.length || targetItemIndex >= newItems.length) {
            console.warn('Warning! No items found at ', sourceItemIndex, targetItemIndex);
            return;
        }

        const sourceItem = newItems[sourceItemIndex];
        const targetItem = newItems[targetItemIndex];

        newItems[targetItemIndex] = sourceItem;
        newItems[sourceItemIndex] = targetItem;

        this._setModelItems(newItems);

    }

    /**
     * Deletes item at index `itemIndex`
     *
     * @param itemIndex
     * @private
     */
    private _deleteItem (itemIndex: number) {

        const oldModel = this.props.model;
        const oldItems = oldModel?.items ?? [];
        const newItems = filter(oldItems, (item: any, index: number) => index !== itemIndex);

        this._setModelItems(newItems);

    }

    /**
     * Opens editor for item at index `itemIndex`
     *
     * @param itemIndex
     * @private
     */
    private _editItem (itemIndex: number) {

        const changeItemCallback  = (value: FormItem) => {
            this._replaceItem(itemIndex, value);
            EditFieldModal.setItem(itemIndex, value);
        }

        const deleteItemCallback  = () => {
            this._deleteItem(itemIndex);
            EditFieldModal.closeModal();
        };

        const model = this.props.model;
        const items = model?.items ?? [];
        const item  = find(items, (item: any, index: number) => index === itemIndex);

        if (item === undefined) {

            LOG.error(`Could not find item by index ${itemIndex}`);

        } else {

            LOG.debug(`_editItem: `, itemIndex, item);

            EditFieldModal.openModal(
                itemIndex,
                item,
                changeItemCallback,
                deleteItemCallback
            );

        }


    }

    private _setModel (model: FormModel) {

        if (this.props.onChange) {
            try {
                this.props.onChange(model);
            } catch (err) {
                console.error('Error while calling onChange prop: ', err);
            }
        } else {
            console.warn('Parent did not define onChange prop');
        }

    }

    private _setModelItems (items: FormItem[]) {

        this._setModel({
            ...this.props.model,
            items
        });

    }

    private _changeTitle (value: string | undefined) {

        this._setModel({
            ...this.props.model,
            title: value ?? ''
        });

    }


    private _onChangeCancelLabel(value: string | undefined) {

        this._setModel({
            ...this.props.model,
            cancelLabel: value ?? ''
        });

        this._closeActionsMenu();

    }

    private _onResetCancelLabel() {
        this._onChangeCancelLabel('');
    }

    private _onDeleteCancelLabel() {

        const {cancelLabel, ...newModel} = this.props.model;

        this._setModel(newModel);

        this._closeActionsMenu();

    }


    private _onChangeSubmitLabel(value: string | undefined) {

        this._setModel({
            ...this.props.model,
            submitLabel: value ?? ''
        });

        this._closeActionsMenu();

    }

    private _onResetSubmitLabel() {
        this._onChangeSubmitLabel('');
    }

    private _onDeleteSubmitLabel() {

        const {submitLabel, ...newModel} = this.props.model;

        this._setModel(newModel);

        this._closeActionsMenu();

    }

    private _onToggleActionsMenu () {

        this.setState((state : FormBuilderState) => ({actionsMenuOpen: !state.actionsMenuOpen}));

    }

    private _closeActionsMenu () {

        this.setState({actionsMenuOpen: false});

    }

}

export default FormBuilder;
